/**
 * Types for Microsoft Security Response Center (MSRC) CVRF API v3.
 * @see https://api.msrc.microsoft.com/cvrf/v3.0/swagger/v3/swagger.json
 */

/** OData response wrapper for a collection (e.g. /Updates). */
export interface MSRCODataResponse<T> {
    '@odata.context'?: string;
    '@odata.count'?: number;
    value?: T[];
}

/** Security update summary (item in /Updates list). */
export interface MSRCUpdateSummary {
    Id?: string;
    Alias?: string;
    DocumentTitle?: string;
    Severity?: string;
    InitialReleaseDate?: string;
    CurrentReleaseDate?: string;
    CvrfUrl?: string;
    [key: string]: unknown;
}

/** Query options for /Updates (OData: $filter, $orderby, $top, $skip, $select, etc.). */
export interface MSRCUpdatesQueryOptions {
    /** OData $filter expression (e.g. on InitialReleaseDate, CurrentReleaseDate). */
    filter?: string;
    /** OData $orderby (e.g. "CurrentReleaseDate desc"). */
    orderBy?: string;
    /** OData $top. */
    top?: number;
    /** OData $skip. */
    skip?: number;
    /** OData $select. */
    select?: string;
}

/** Single revision entry in CVRF DocumentTracking. */
export interface CvrfRevision {
    number?: string;
    date?: string;
    description?: string;
}

/** Document tracking info parsed from CVRF XML. */
export interface CvrfDocumentTracking {
    id?: string;
    status?: string;
    version?: string;
    initialReleaseDate?: string;
    currentReleaseDate?: string;
    revisions?: CvrfRevision[];
}

/** Single note in CVRF DocumentNotes. */
export interface CvrfNote {
    title?: string;
    type?: string;
    ordinal?: string;
    value: string;
}

/** Product or branch info from CVRF ProductTree. */
export interface CvrfProduct {
    productId?: string;
    fullProductName?: string;
    branch?: string;
}

/** Vulnerability entry parsed from CVRF XML. */
export interface CvrfVulnerability {
    title?: string;
    cve?: string;
    notes?: string[];
    severity?: string;
    remediation?: string;
    productStatuses?: string[];
}

/**
 * Parsed CVRF document (XML body from GET /cvrf/{id}).
 * Endpoint returns plain/text; the body is XML.
 */
export class CvrfDocumentInfo {
    documentTitle: string = '';
    documentType: string = '';
    publisherName: string = '';
    publisherContact?: string;
    tracking: CvrfDocumentTracking = {};
    notes: CvrfNote[] = [];
    products: CvrfProduct[] = [];
    vulnerabilities: CvrfVulnerability[] = [];

    static getText(el: Element | null): string {
        if (!el) return '';
        const t = el.textContent?.trim() ?? '';
        return t;
    }

    static getFirstByLocalName(parent: Element, localName: string): Element | null {
        const byNs = parent.getElementsByTagNameNS('*', localName);
        if (byNs.length > 0) return byNs[0];
        const byTag = parent.getElementsByTagName(localName);
        return byTag.length > 0 ? byTag[0] : null;
    }

    static getAllByLocalName(parent: Element, localName: string): Element[] {
        const byNs = parent.getElementsByTagNameNS('*', localName);
        if (byNs.length > 0) return Array.from(byNs);
        const byTag = parent.getElementsByTagName(localName);
        return Array.from(byTag);
    }

    /**
     * Parse CVRF XML string (plain/text response body) into CvrfDocumentInfo.
     */
    static parseFromXml(xmlText: string): CvrfDocumentInfo {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlText, 'text/xml');
        const root = doc.documentElement;
        const info = new CvrfDocumentInfo();

        const byLocal = (name: string) => CvrfDocumentInfo.getFirstByLocalName(root, name);
        const text = (name: string) => CvrfDocumentInfo.getText(byLocal(name));

        info.documentTitle = text('DocumentTitle') || text('documentTitle');
        info.documentType = text('DocumentType') || text('documentType');
        const pub = byLocal('DocumentPublisher');
        if (pub) {
            info.publisherName = CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(pub, 'Name') ?? CvrfDocumentInfo.getFirstByLocalName(pub, 'name')) || '';
            info.publisherContact = CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(pub, 'ContactDetails') ?? CvrfDocumentInfo.getFirstByLocalName(pub, 'contactDetails')) || undefined;
        }

        const trackingEl = byLocal('DocumentTracking') ?? byLocal('documentTracking');
        if (trackingEl) {
            const tr = trackingEl;
            const idEl = CvrfDocumentInfo.getFirstByLocalName(tr, 'Identification') ?? CvrfDocumentInfo.getFirstByLocalName(tr, 'identification');
            const idInner = idEl ? (CvrfDocumentInfo.getFirstByLocalName(idEl, 'ID') ?? CvrfDocumentInfo.getFirstByLocalName(idEl, 'Id')) : null;
            info.tracking = {
                id: idInner ? CvrfDocumentInfo.getText(idInner) : undefined,
                status: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(tr, 'Status') ?? CvrfDocumentInfo.getFirstByLocalName(tr, 'status')),
                version: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(tr, 'Version') ?? CvrfDocumentInfo.getFirstByLocalName(tr, 'version')),
                initialReleaseDate: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(tr, 'InitialReleaseDate') ?? CvrfDocumentInfo.getFirstByLocalName(tr, 'initialReleaseDate')),
                currentReleaseDate: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(tr, 'CurrentReleaseDate') ?? CvrfDocumentInfo.getFirstByLocalName(tr, 'currentReleaseDate')),
            };
            const revHistory = CvrfDocumentInfo.getFirstByLocalName(tr, 'RevisionHistory') ?? CvrfDocumentInfo.getFirstByLocalName(tr, 'revisionHistory');
            if (revHistory) {
                const revs = CvrfDocumentInfo.getAllByLocalName(revHistory, 'Revision').length
                ? CvrfDocumentInfo.getAllByLocalName(revHistory, 'Revision')
                : CvrfDocumentInfo.getAllByLocalName(revHistory, 'revision');
                info.tracking.revisions = Array.from(revs).map((r) => ({
                    number: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(r, 'Number') ?? CvrfDocumentInfo.getFirstByLocalName(r, 'number')),
                    date: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(r, 'Date') ?? CvrfDocumentInfo.getFirstByLocalName(r, 'date')),
                    description: CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(r, 'Description') ?? CvrfDocumentInfo.getFirstByLocalName(r, 'description')),
                }));
            }
        }

        const notesEl = byLocal('DocumentNotes') ?? byLocal('documentNotes');
        if (notesEl) {
            const noteList = CvrfDocumentInfo.getAllByLocalName(notesEl, 'Note').length
                ? CvrfDocumentInfo.getAllByLocalName(notesEl, 'Note')
                : CvrfDocumentInfo.getAllByLocalName(notesEl, 'note');
            info.notes = Array.from(noteList).map((n) => ({
                title: n.getAttribute?.('Title') ?? n.getAttribute?.('title') ?? undefined,
                type: n.getAttribute?.('Type') ?? n.getAttribute?.('type') ?? undefined,
                ordinal: n.getAttribute?.('Ordinal') ?? n.getAttribute?.('ordinal') ?? undefined,
                value: CvrfDocumentInfo.getText(n),
            }));
        }

        const productTree = byLocal('ProductTree') ?? byLocal('productTree');
        if (productTree) {
            const fullNames = CvrfDocumentInfo.getAllByLocalName(productTree, 'FullProductName').length
                ? CvrfDocumentInfo.getAllByLocalName(productTree, 'FullProductName')
                : CvrfDocumentInfo.getAllByLocalName(productTree, 'fullProductName');
            info.products = Array.from(fullNames).map((el, i) => ({
                productId: el.getAttribute?.('ProductID') ?? el.getAttribute?.('productId') ?? String(i),
                fullProductName: CvrfDocumentInfo.getText(el),
            }));
        }

        const vulnList = CvrfDocumentInfo.getAllByLocalName(root, 'Vulnerability').length
                ? CvrfDocumentInfo.getAllByLocalName(root, 'Vulnerability')
                : CvrfDocumentInfo.getAllByLocalName(root, 'vulnerability');
        info.vulnerabilities = Array.from(vulnList).map((v) => {
            const title = CvrfDocumentInfo.getText(CvrfDocumentInfo.getFirstByLocalName(v, 'Title') ?? CvrfDocumentInfo.getFirstByLocalName(v, 'title'));
            const cveEl = CvrfDocumentInfo.getFirstByLocalName(v, 'CVE') ?? CvrfDocumentInfo.getFirstByLocalName(v, 'cve');
            const cve = cveEl ? CvrfDocumentInfo.getText(cveEl) : undefined;
            const noteEls = CvrfDocumentInfo.getAllByLocalName(v, 'Note').length
                ? CvrfDocumentInfo.getAllByLocalName(v, 'Note')
                : CvrfDocumentInfo.getAllByLocalName(v, 'note');
            const notes = Array.from(noteEls).map((n) => CvrfDocumentInfo.getText(n)).filter(Boolean);
            return {
                title,
                cve,
                notes: notes.length ? notes : undefined,
                severity: undefined,
                remediation: undefined,
                productStatuses: undefined,
            };
        });

        return info;
    }
}
